import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import MetaCorner from '../hero/MetaCorner';
import { useLang } from '../../context/LangContext';
import { fr } from '../../i18n/fr';
import { en } from '../../i18n/en';
import { de } from '../../i18n/de';

const T = { fr, en, de };
const EASE = [0.16, 1, 0.3, 1];

const SECTION_IDS = ['s0', 's1', 's2', 's3', 's4', 's5', 's6'];

export default function Privacy() {
  const { lang } = useLang();
  const t = T[lang]?.privacy || T.fr.privacy;

  // Scroll to top on mount (privacy can be deep-linked; ensure clean view).
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  const handleReturn = (e) => {
    e.preventDefault();
    // Navigate to root: clears the hash and triggers hashchange.
    if (window.location.hash) {
      window.history.pushState(null, '', window.location.pathname + window.location.search);
      window.dispatchEvent(new HashChangeEvent('hashchange'));
    }
  };

  return (
    <main
      style={{
        position: 'relative',
        minHeight: '100vh',
        background: 'var(--bg)',
        color: 'rgba(255,255,255,0.75)',
        paddingTop: 'clamp(120px, 14vw, 180px)',
        paddingBottom: 'clamp(140px, 14vw, 200px)',
        overflow: 'hidden',
      }}
    >
      {/* Subtle noise overlay for cohesion with Hero */}
      <div className="noise" aria-hidden="true" style={{ zIndex: 1 }} />

      {/* Meta corners */}
      <MetaCorner position="TL" animate hideOnMobile>
        <button
          onClick={handleReturn}
          style={{
            background: 'none',
            border: 'none',
            fontFamily: 'var(--mono)',
            fontSize: 'clamp(10px, 0.85vw, 12px)',
            letterSpacing: '0.28em',
            textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.55)',
            cursor: 'none',
            display: 'inline-block',
            padding: '4px 0',
          }}
        >
          {t.return}
        </button>
      </MetaCorner>
      <MetaCorner position="TR" animate hideOnMobile lines={[t.edition, t.updated]} />
      <MetaCorner position="BL" animate hideOnMobile lines={['TSANGA', 'CSC RECORDS']} />
      <MetaCorner position="BR" animate hideOnMobile lines={['50.85°N 4.35°E', 'BRUXELLES']} />

      {/* Central column */}
      <div
        style={{
          position: 'relative',
          zIndex: 10,
          maxWidth: 720,
          margin: '0 auto',
          padding: '0 clamp(20px, 5vw, 40px)',
        }}
      >
        {/* Mobile return link (since corners are hidden on mobile) */}
        <button
          onClick={handleReturn}
          className="privacy-return-mobile"
          style={{
            display: 'none',
            background: 'none',
            border: 'none',
            fontFamily: 'var(--mono)',
            fontSize: 11,
            letterSpacing: '0.28em',
            textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.55)',
            cursor: 'pointer',
            marginBottom: 32,
            padding: '8px 0',
          }}
        >
          {t.return}
        </button>

        {/* Title */}
        <motion.h1
          initial={{ opacity: 0, y: 24, filter: 'blur(8px)' }}
          animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
          transition={{ duration: 1.2, ease: EASE }}
          style={{
            fontFamily: 'var(--serif)',
            fontWeight: 300,
            fontSize: 'clamp(48px, 8vw, 96px)',
            lineHeight: 0.95,
            letterSpacing: '-0.02em',
            margin: 0,
            color: 'rgba(255,255,255,0.95)',
            whiteSpace: 'pre-line',
          }}
        >
          {t.title}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.9, ease: EASE }}
          style={{
            marginTop: 'clamp(20px, 2vw, 28px)',
            fontFamily: 'var(--mono)',
            fontSize: 'clamp(11px, 0.95vw, 13px)',
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.45)',
          }}
        >
          {t.subtitle}
        </motion.p>

        {/* Sections */}
        <div style={{ marginTop: 'clamp(60px, 8vw, 100px)', display: 'flex', flexDirection: 'column', gap: 'clamp(40px, 5vw, 60px)' }}>
          {SECTION_IDS.map((id, i) => (
            <motion.section
              key={id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 + i * 0.08, duration: 0.7, ease: EASE }}
            >
              <header style={{ display: 'flex', gap: 16, alignItems: 'baseline', marginBottom: 20 }}>
                <span
                  style={{
                    fontFamily: 'var(--mono)',
                    fontSize: 'clamp(10px, 0.85vw, 12px)',
                    letterSpacing: '0.28em',
                    color: 'rgba(255,255,255,0.4)',
                  }}
                >
                  [{String(i + 1).padStart(2, '0')}]
                </span>
                <h2
                  style={{
                    margin: 0,
                    fontFamily: 'var(--mono)',
                    fontSize: 'clamp(13px, 1.1vw, 16px)',
                    letterSpacing: '0.18em',
                    textTransform: 'uppercase',
                    color: 'rgba(255,255,255,0.9)',
                    fontWeight: 500,
                  }}
                >
                  {t[`${id}Title`]}
                </h2>
              </header>
              <p
                style={{
                  margin: 0,
                  fontFamily: 'var(--mono)',
                  fontSize: 'clamp(13px, 1vw, 15px)',
                  lineHeight: 1.75,
                  color: 'rgba(255,255,255,0.7)',
                  maxWidth: '62ch',
                }}
              >
                {t[`${id}Body`]}
              </p>
            </motion.section>
          ))}
        </div>
      </div>
    </main>
  );
}
